import { storeToRefs } from 'pinia'
import { ComputedRef, computed } from 'vue'

import { permissionAndSettingsStore } from '@/entities/settings-permission-and-services'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useStoreConfiguration } from '@/shared/store/configuration'
import {
  FeaturesNames,
} from '@/shared/types/settings'

import {
  resolveCallDetailingEnabled,
  resolveChatMessagingUiEnabled,
  resolveWebClientUiEnabled,
} from './access-ui-resolvers'
import { useFeatureAccessEmulatorStore } from './feature-access-emulator'

export const useUserFeatures = () => {
  const { getEnabledFeatures, areAccessFeaturesLoaded } = storeToRefs(
    permissionAndSettingsStore.useStoreSettingsServicesAndPackages(),
  )
  const { filesEnabled } = storeToRefs(useStoreConfiguration())
  const { currentUser } = useCurrentUser()
  const featureAccessEmulator = useFeatureAccessEmulatorStore()

  const enabledFeatures: ComputedRef<Set<FeaturesNames>> = computed(() => {
    const features = new Set(getEnabledFeatures.value)
    features.add(FeaturesNames.DEFAULT)
    if (filesEnabled.value) {
      features.add(FeaturesNames.WEBCLIENT_FILES)
    } else if (features.has(FeaturesNames.WEBCLIENT_FILES)) {
      features.delete(FeaturesNames.WEBCLIENT_FILES)
    }

    return features
  })

  const hasFeature = (feature: FeaturesNames) => {
    const apiValue = enabledFeatures.value.has(feature)
    return featureAccessEmulator.resolveFeature(feature, apiValue)
  }

  const needToShowDialServicesLink = computed(() => {
    return hasFeature(FeaturesNames.WEB_CALL_BACKORDER) ||
      hasFeature(FeaturesNames.WEB_TO_FAX) ||
      featureAccessEmulator.resolveWebSoftphone(Boolean(currentUser.value?.webSoftphoneEnabled))
  })

  /** Веб-клиент: звонки, контакты, календарь, уведомления (WUI-4375). */
  const isWebClientUiEnabled = computed(() => resolveWebClientUiEnabled())

  /** Чаты в UI: полный UC — веб-клиент + InstantMessaging (WUI-4375, WUI-690). */
  const isChatMessagingUiEnabled = computed(() => resolveChatMessagingUiEnabled())

  /** Детализация вызовов (CDRsAccess): выгрузка и полная история (WUI-4375). */
  const isCallDetailingEnabled = computed(() => resolveCallDetailingEnabled())

  return {
    enabledFeatures,
    needToShowDialServicesLink,
    isWebClientUiEnabled,
    isChatMessagingUiEnabled,
    isCallDetailingEnabled,
    areAccessFeaturesLoaded,
    hasFeature,
  }
}
